import useSWR from "swr";
import { fetcher } from "lib/actions/fetcher";
import { endpointsAssets } from "lib/actions/endpoints/endpointsAssets";
import { useWorkspaces } from "lib/hooks/useWorkspaces";
import { useRouteParamNumber } from "./useRouteParam";
import { WorkspaceModel } from "lib/types/workspaces/WorkspaceModel";
import { WorkspaceDetailedUsage } from "lib/types/workspaces/WorkspaceDetailedUsage";

export function useWorkspace(workspaceId: number) {
  const { workspaces, isLoading, hasError, mutate } = useWorkspaces();

  const workspace = workspaces?.find((p) => p.id === workspaceId);

  return {
    workspace,
    isLoading,
    hasError,
    mutate: async (
      mutator?: (current: WorkspaceModel) => WorkspaceModel,
      revalidate: boolean = true,
    ) => {
      const workspaces = mutator
        ? await mutate((current) =>
            current?.map(
              (w: WorkspaceModel) => (w.id === workspaceId ? mutator(w) : w),
              {
                revalidate,
              },
            ),
          )
        : await mutate();
      return workspaces?.find((w: WorkspaceModel) => w.id == workspaceId);
    },
  };
}

export function useWorkspaceDetailedUsage() {
  const workspaceId = useCurrentWorkspaceId();
  const {
    data: workspaceUsage,
    error,
    mutate,
  } = useSWR<WorkspaceDetailedUsage>(
    workspaceId && endpointsAssets.workspaceStorageDetailed(workspaceId),
    fetcher,
  );

  return {
    workspaceUsage,
    isLoading: !workspaceUsage && !error,
    hasError: !!error,
    mutate,
  };
}

// Consider rewriting to only get the workspace that we need instead of filtering all workspaces on the client-side
export function useCurrentWorkspace() {
  const workspaceId = useCurrentWorkspaceId();
  return {
    workspaceId,
    isOneOpen: workspaceId != null,
    ...useWorkspace(workspaceId),
  };
}

export function useCurrentWorkspaceId(): number | null {
  const [id] = useRouteParamNumber("workspaceId");
  return id;
}
